import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, IMAGES, FIRM } from "@/lib/site";
import { cn } from "@/lib/utils";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-300",
        scrolled
          ? "bg-navy-950/80 backdrop-blur-md shadow-[0_10px_30px_-12px_rgba(0,0,0,0.6)]"
          : "bg-navy-950"
      )}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="relative flex items-end justify-between gap-6 py-3">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo-link"
            className="group shrink-0"
          >
            <img
              src={IMAGES.logo}
              alt="Mahaney Law"
              className="logo-light h-11 sm:h-12 lg:h-[4.75rem] w-auto"
            />
          </Link>

          {/* Centered nav (lowered) */}
          <nav className="hidden lg:flex flex-1 items-center justify-center gap-6 xl:gap-8 px-4 pb-2">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  cn(
                    "relative py-1.5 text-[0.8rem] font-semibold uppercase tracking-[0.12em] whitespace-nowrap transition-colors",
                    isActive ? "text-gold" : "text-white/85 hover:text-white"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-gold" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right: phone above CTA */}
          <div className="hidden lg:flex flex-col items-end gap-2.5 shrink-0">
            <a
              href={`tel:${FIRM.phoneHref}`}
              data-testid="nav-call-link"
              className="flex items-center gap-2 text-sm font-semibold text-white hover:text-gold transition-colors whitespace-nowrap"
            >
              <Phone className="h-4 w-4 text-gold" />
              {FIRM.phone}
            </a>
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="px-7 py-2.5 rounded-md bg-gold text-navy-950 text-sm font-bold uppercase tracking-wide hover:bg-gold-light transition-colors whitespace-nowrap"
            >
              Free Consultation
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-sm text-white hover:bg-white/10"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        <div
          className={cn(
            "lg:hidden overflow-hidden transition-[max-height,opacity] duration-500",
            open ? "max-h-[520px] opacity-100 pb-4" : "max-h-0 opacity-0"
          )}
        >
          <div className="rounded-sm border border-white/10 bg-navy-900 p-5 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  cn(
                    "px-3 py-3 rounded-sm text-sm font-semibold uppercase tracking-wide",
                    isActive
                      ? "bg-gold text-navy-950"
                      : "text-white/85 hover:bg-white/10"
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-white/10">
              <a
                href={`tel:${FIRM.phoneHref}`}
                data-testid="mobile-call-link"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-sm border border-white/20 text-white text-sm font-semibold"
              >
                <Phone className="h-4 w-4 text-gold" />
                {FIRM.phone}
              </a>
              <Link
                to="/contact"
                data-testid="mobile-cta-button"
                className="block text-center px-5 py-3 rounded-sm bg-gold text-navy-950 text-sm font-bold uppercase tracking-wide"
              >
                Free Consultation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
